import { useMemo, type ReactNode } from "react";
import { useAppSelector } from "@/stores/hook";
import usePipeline from "@/features/stage/api/usePipeline";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  EMPTY_REQUIRED_FIELD_MARK_INDEX,
  RequiredFieldMarksContext,
  buildRequiredFieldMarkIndex,
  mergeOrgRequiredIntoIndex,
  useTenantRequirements,
  type PipelinePosition,
  type RequiredFieldMark,
} from "./requiredFieldMarks";

/**
 * Components for the stage-requirement stars. The rule, the resolver, the
 * context and the `useRequiredFieldMark` hook live in `requiredFieldMarks.ts`
 * (kept apart so fast-refresh sees a components-only module here; the `.ui`
 * suffix keeps the two names distinct on a case-insensitive filesystem).
 */

/**
 * Provide marks for an explicit pipeline position. `position: null` = the
 * creation position (Add Lead): every enforceable gate applies.
 */
export function RequiredFieldMarksProvider({
  position,
  orgRequiredKeys,
  disabled = false,
  children,
}: {
  position: PipelinePosition | null;
  /**
   * Tenant-wide Add Lead policy keys ("source:leadSource", …) to star as
   * well. Only the creation form passes these — the policy is enforced on
   * create, not on edit, so the drawer stays stage-gates-only.
   */
  orgRequiredKeys?: Iterable<string> | null;
  /**
   * Render NO marks (an empty index) while still mounting this same
   * component. The drawer uses it before its entity has loaded — it must
   * not swap element types at that moment (see ActiveEntityRequiredFieldMarks).
   */
  disabled?: boolean;
  children: ReactNode;
}) {
  const { data: requirements } = useTenantRequirements();
  const { pipeline } = usePipeline();
  const viewerRoleId = useAppSelector(
    (state) => state.auth.currentUser?.user?.role?.id ?? null,
  );
  const stageId = position?.stageId ?? null;
  const statusId = position?.statusId ?? null;
  // Stable identity for the memo: an Iterable/Set from the caller is a new
  // object per render, but the joined key list is not.
  const orgKeySig = [...(orgRequiredKeys ?? [])].sort().join("|");

  const index = useMemo(
    () =>
      disabled
        ? EMPTY_REQUIRED_FIELD_MARK_INDEX
        : mergeOrgRequiredIntoIndex(
            buildRequiredFieldMarkIndex(
              requirements,
              pipeline,
              stageId != null && statusId != null ? { stageId, statusId } : null,
              viewerRoleId != null ? Number(viewerRoleId) : null,
            ),
            orgKeySig ? orgKeySig.split("|") : null,
          ),
    [disabled, requirements, pipeline, stageId, statusId, orgKeySig, viewerRoleId],
  );

  return (
    <RequiredFieldMarksContext.Provider value={index}>
      {children}
    </RequiredFieldMarksContext.Provider>
  );
}

/**
 * Drawer flavour: binds the marks to whichever entity the drawer is working
 * on — the deal when one is active, else the lead. Reads the two form
 * contexts directly (NOT `useActiveDataMutation`, which `watch()`es every
 * field and would re-render the whole drawer per keystroke); the memoised
 * index only changes when the position does.
 */
export function ActiveEntityRequiredFieldMarks({
  children,
}: {
  children: ReactNode;
}) {
  const { lead } = useLeadForm();
  const childCtx = useChildForm();
  const active: unknown = childCtx?.currentChildId ? childCtx.child : lead;
  const position = readPipelinePosition(active);

  // ALWAYS the same element type. The first version of this rendered a bare
  // Context.Provider until the entity loaded and then switched to
  // RequiredFieldMarksProvider — a different component at the same tree
  // position, which makes React unmount and remount EVERYTHING underneath
  // (the whole lead drawer) the instant the lead arrives: the drawer opened,
  // vanished, and opened again ("coming and going", 2026-09-07 prod). Until
  // the entity loads we render no marks (not the creation set, which would
  // star everything for a beat) via `disabled` — same component, empty index.
  return (
    <RequiredFieldMarksProvider position={position} disabled={!position}>
      {children}
    </RequiredFieldMarksProvider>
  );
}

/** `{ stageId, statusId }` off a lead/deal row, or null until it has loaded. */
function readPipelinePosition(entity: unknown): PipelinePosition | null {
  if (!entity || typeof entity !== "object") return null;
  const e = entity as {
    stageId?: unknown;
    statusId?: unknown;
    stage?: { id?: unknown } | null;
    status?: { id?: unknown } | null;
  };
  const stageId = Number(e.stageId ?? e.stage?.id);
  const statusId = Number(e.statusId ?? e.status?.id);
  if (!Number.isFinite(stageId) || stageId <= 0 || !Number.isFinite(statusId)) {
    return null;
  }
  return { stageId, statusId };
}

/**
 * The star itself. Red for a blocking gate, amber for warn-only; the tooltip
 * says where the field is first needed. Renders nothing for a null mark, so
 * it can be dropped into any label unconditionally.
 */
export function RequiredStar({
  mark,
  className = "",
}: {
  mark: RequiredFieldMark | null | undefined;
  className?: string;
}) {
  if (!mark) return null;
  const text =
    mark.reason ??
    (mark.blocking
      ? `Required to reach ${mark.where}`
      : `Recommended before ${mark.where} (warning only)`);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={text}
          className={`ml-0.5 inline-block cursor-help select-none font-medium leading-none ${
            mark.blocking ? "text-red-500" : "text-amber-500"
          } ${className}`}
        >
          *
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{text}</TooltipContent>
    </Tooltip>
  );
}
